import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  MessageCircle, 
  Users, 
  Briefcase, 
  Clock, 
  Calendar,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  User
} from 'lucide-react';
import { useAuth } from '../App';
import CollabRequestForm from '../components/CollabRequestForm';

const BACKEND_URL = '';  // Use relative URLs

const CollabRequestDetail = () => {
  const { requestId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadRequest();
  }, [requestId]);

  const loadRequest = async () => {
    try {
      const response = await axios.get('/api/collab-requests/${requestId}');
      setRequest(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading collab request:', error);
      setLoading(false);
    }
  };

  const handleMessageCreator = async () => {
    if (!user) {
      alert('Please sign in to message creators');
      navigate('/auth');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/messages/conversation/create',
        { recipient_id: request.creator_id },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Navigate to messages with conversation ID
      navigate(`/messages?conversation=${response.data.id}`);
    } catch (error) {
      console.error('Error creating conversation:', error);
      alert(error.response?.data?.detail || 'Failed to start conversation');
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        '/api/collab-requests/${requestId}/status',
        { status: newStatus },
        {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        }
      );
      
      setRequest(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error('Error updating status:', error);
      alert(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        '/api/collab-requests/${requestId}',
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      navigate('/collab-board');
    } catch (error) {
      console.error('Error deleting request:', error);
      alert(error.response?.data?.detail || 'Failed to delete request');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Open': return 'bg-green-500';
      case 'In Progress': return 'bg-yellow-500';
      case 'Closed': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const isOwner = user && request && user.id === request.creator_id;

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
          <p className="text-gray-400 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-black pt-16 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Request Not Found</h2>
          <p className="text-gray-400 mb-6">This collaboration request doesn't exist or has been deleted.</p>
          <button
            onClick={() => navigate('/collab-board')}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-6 py-3 rounded-lg font-semibold"
          >
            Back to Collab Board
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-16 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Back Button */}
        <button
          onClick={() => navigate('/collab-board')}
          className="flex items-center space-x-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Collab Board</span>
        </button>

        {/* Main Content */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
          
          {/* Header */}
          <div className="p-6 border-b border-gray-800">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <span className={`${getStatusColor(request.status)} text-white text-sm font-bold px-3 py-1 rounded-full`}>
                    {request.status}
                  </span>
                  <span className="text-gray-500 text-sm">
                    Posted {new Date(request.created_at).toLocaleDateString()}
                  </span>
                </div>
                <h1 className="text-3xl font-bold text-white mb-4">
                  {request.title}
                </h1>
              </div>

              {/* Owner Controls */}
              {isOwner && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowEditForm(true)}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-800 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>

            {/* Creator Info */}
            <div className="flex items-center justify-between">
              <div
                onClick={() => navigate(`/u/${request.creator_id}`)}
                className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity"
              >
                {request.creator_profile_image ? (
                  <img
                    src={request.creator_profile_image}
                    alt={request.creator_username}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                    <span className="text-white font-bold">
                      {request.creator_username.charAt(0)}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-white font-semibold">
                    {request.creator_username}
                  </p>
                  <p className="text-sm text-gray-400">View Profile</p>
                </div>
              </div>

              {!isOwner && user && (
                <button
                  onClick={handleMessageCreator}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 transition-all"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>Message Creator</span>
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            
            {/* Description */}
            <div>
              <h2 className="text-xl font-bold text-white mb-3">About This Project</h2>
              <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                {request.description}
              </p>
            </div>

            {/* Looking For */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center space-x-2">
                <Users className="w-5 h-5" />
                <span>Looking For</span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {request.looking_for.map((role, idx) => (
                  <span
                    key={idx}
                    className="bg-orange-500/20 text-orange-400 px-4 py-2 rounded-lg font-medium"
                  >
                    {role}
                  </span>
                ))}
              </div>
            </div>

            {/* Genre */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Genre</h3>
              <div className="flex flex-wrap gap-2">
                {request.genre.map((g, idx) => (
                  <span
                    key={idx}
                    className="bg-purple-500/20 text-purple-400 px-4 py-2 rounded-lg font-medium"
                  >
                    {g}
                  </span>
                ))}
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              {/* Compensation */}
              <div className="bg-gray-800 p-4 rounded-lg">
                <div className="flex items-center space-x-2 text-gray-400 mb-2">
                  <Briefcase className="w-5 h-5" />
                  <span className="text-sm font-medium">Compensation</span>
                </div>
                <p className="text-white font-semibold text-lg">
                  {request.compensation}
                </p>
              </div>

              {/* Timeline */}
              {request.timeline && (
                <div className="bg-gray-800 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 text-gray-400 mb-2">
                    <Clock className="w-5 h-5" />
                    <span className="text-sm font-medium">Timeline</span>
                  </div>
                  <p className="text-white font-semibold text-lg">
                    {request.timeline}
                  </p>
                </div>
              )}
            </div>

            {/* Owner Status Controls */}
            {isOwner && (
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-400 mb-3">Update Status</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleUpdateStatus('Open')}
                    disabled={request.status === 'Open'}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      request.status === 'Open'
                        ? 'bg-green-500 text-white cursor-default'
                        : 'bg-gray-700 text-gray-300 hover:bg-green-500 hover:text-white'
                    }`}
                  >
                    Open
                  </button>
                  <button
                    onClick={() => handleUpdateStatus('In Progress')}
                    disabled={request.status === 'In Progress'}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      request.status === 'In Progress'
                        ? 'bg-yellow-500 text-white cursor-default'
                        : 'bg-gray-700 text-gray-300 hover:bg-yellow-500 hover:text-white'
                    }`}
                  >
                    In Progress
                  </button>
                  <button
                    onClick={() => handleUpdateStatus('Closed')}
                    disabled={request.status === 'Closed'}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      request.status === 'Closed'
                        ? 'bg-gray-500 text-white cursor-default'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-500 hover:text-white'
                    }`}
                  >
                    Closed
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Form Modal */}
      {showEditForm && (
        <CollabRequestForm
          existingRequest={request}
          onClose={() => setShowEditForm(false)}
          onSuccess={() => {
            setShowEditForm(false);
            loadRequest();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-white mb-4">Delete Request?</h3>
            <p className="text-gray-400 mb-6">
              Are you sure you want to delete this collaboration request? This action cannot be undone.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={handleDelete}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollabRequestDetail;
